import { createTheme, styled, alpha, darken } from '@mui/material/styles';
import { Rating } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { esES } from '@mui/x-date-pickers/locales';
import es from 'date-fns/locale/es';

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.primary.light,
  },
  '& .MuiRating-iconHover': {
    color: theme.palette.primary.light,
  },
}));

const theme = createTheme({
  palette: {
    primary: {
      main: '#0E2465',
    },
    secondary: {
      main: '#7495F8',
      // La letra sobre el azul medio (botones, páginas elegidas y fichas de color="secondary"): el azul oscuro de primary, 5.1:1 (en
      // blanco daba 2.8:1)
      contrastText: '#0E2465'
    },
    // `claro` y `oscuro` van aparte de `light` y `dark`, que MUI calcula de `main` para sus avisos y botones de color. `oscuro`: el
    // texto y los íconos de ese color sobre fondos claros (el verde, el amarillo y el rojo de la paleta son claros y no se leen como letra)
    success: {
      main: '#59C39A',
      // El verde claro de «Confirmado» y «Activa» (fondos y bordes; el texto encima, en primary.main)
      claro: '#9DE1D5',
      oscuro: '#1B7A5A',   // 5.3:1 sobre blanco
    },
    error: {
      main: '#FF827A',
      contrastText: '#FFFFFF',
      oscuro: '#B23B33',   // 5.9:1 sobre blanco (el rojo de la paleta como texto da 2.4:1)
    },
    warning: {
      main: '#FFD074',
      oscuro: '#92400E',   // 7.1:1 sobre blanco y 4.9:1 sobre el amarillo de la paleta
    },
    info: {
      main: '#80A3FF'
    },
    text: {
      primary: "#393939",
      // El gris de los textos secundarios: color="text.secondary" (o color: 'text.secondary' en sx), nunca el hex. Con él pinta MUI
      // los radios y casillas sin marcar, el texto de ayuda de los campos, los avisos del autocompletar y el texto de los diálogos.
      // Da 5.1:1 sobre blanco y 4.5:1 sobre el gris del fondo de la página (#F2F2F2, public/index.html): para texto chico se pide 4.5:1
      secondary: "#676D8A",
    },
    // Bordes y fondos sutiles (en sx: borderColor: 'sutil', bgcolor: 'sutil')
    sutil: '#F1F5F9',
    // Los colores de otras marcas, solo para sus íconos (compartir en WhatsApp o Facebook)
    marcas: {
      whatsapp: '#25D366',
      facebook: '#1877F2',
    },
  },

  typography: {
    fontFamily: 'Roboto',
    fontSize: 16,
    grayOp: {
      color: '#212121',
      opacity: 0.8
    },
    h1: {
      fontSize: 56
    },
    h2: {
      fontSize: 48
    },
    h3: {
      fontSize: 32
    },
    body2: {
      fontSize: 13
    },
    caption: {
      fontSize: 11
    }
  },

  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', // Igual que Typography
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          height: 40,
        },
        // MUI pinta el botón con variables (--variant-containedBg…): aquí se cambian por color. El rojo de la paleta es claro: los botones
        // rojos rellenos van en el rojo oscuro con letra blanca (5.9:1; la blanca sobre el rojo claro daba 2.4:1) y los que no tienen
        // relleno, con la letra y el borde en el rojo oscuro (el rojo claro como letra da 2.4:1)
        containedError: ({ theme }) => ({
          '--variant-containedBg': theme.palette.error.oscuro,
          '@media (hover: hover)': { '&:hover': { '--variant-containedBg': darken(theme.palette.error.oscuro, 0.2) } },
        }),
        outlinedError: ({ theme }) => ({
          '--variant-outlinedColor': theme.palette.error.oscuro,
          '--variant-outlinedBorder': alpha(theme.palette.error.oscuro, 0.5),
          '@media (hover: hover)': {
            '&:hover': { '--variant-outlinedBorder': theme.palette.error.oscuro, '--variant-outlinedBg': alpha(theme.palette.error.oscuro, 0.04) },
          },
        }),
        textError: ({ theme }) => ({
          '--variant-textColor': theme.palette.error.oscuro,
          '@media (hover: hover)': { '&:hover': { '--variant-textBg': alpha(theme.palette.error.oscuro, 0.04) } },
        }),
        // Los azules rellenos llevan la letra en el azul oscuro: al pasar el mouse se aclaran, porque oscurecerlos (lo de MUI) la dejaba
        // en 2.7:1
        containedSecondary: ({ theme }) => ({
          '@media (hover: hover)': { '&:hover': { '--variant-containedBg': theme.palette.secondary.light } },
        }),
        outlinedSecondary: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderColor: theme.palette.text.secondary,
          '&:hover': {
            borderColor: '#8F95B2',
            backgroundColor: 'transparent',
            color: '#8F95B2'
          }
        }),
        outlinedSizeLarge: {
          height: 56,
        },
        containedSizeLarge: {
          height: 56,
        },
      }
    },

    // Lo que MUI pinta por su cuenta con el rojo de la paleta va en el rojo oscuro: los contadores (con letra blanca, 5.9:1) y los campos
    // con error (su aviso, su etiqueta y su borde; el aviso en el rojo claro daba 2.4:1)
    MuiBadge: {
      styleOverrides: {
        colorError: ({ theme }) => ({ backgroundColor: theme.palette.error.oscuro }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({ '&.Mui-error': { color: theme.palette.error.oscuro } }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({ '&.Mui-error': { color: theme.palette.error.oscuro } }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({ '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.error.oscuro } }),
      },
    },

    // La página elegida de una paginación azul (color="secondary"), con la letra en el azul oscuro: se aclara al pasar el mouse, como los
    // botones azules
    MuiPaginationItem: {
      styleOverrides: {
        textSecondary: ({ theme }) => ({
          '&.Mui-selected:hover': {
            backgroundColor: theme.palette.secondary.light,
            '@media (hover: none)': { backgroundColor: theme.palette.secondary.main },
          },
          '&.Mui-selected.Mui-focusVisible': { backgroundColor: theme.palette.secondary.light },
        }),
      },
    },

    MuiInputLabel: {
      styleOverrides:{
        root:{
          color: "#404875"
        }
      }
    },
    // Los calendarios son oscuros: lo que MUI pinta en ellos con text.secondary (los días de la semana, el aro de hoy y el título de
    // arriba) va en blanco, como el resto, y lo que pinta con el azul oscuro (los botones «Cancelar» y «OK», el día y el año elegidos)
    // va en el azul medio, que sí se ve sobre el negro
    MuiPickersLayout:{
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: "#181818 !important",
          '& .MuiPickersLayout-actionBar .MuiButton-root': {
            color: theme.palette.secondary.main
          }
        })
      }
    },
    MuiPickersToolbarText: {
      styleOverrides: {
        root: {
          "&.Mui-selected":{
            color: '#FFFFFF'
          },
          color: '#A3A9CC'
        },
      }
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: '#FFFFFF'
        }
      }
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: {
          "&.Mui-disabled":{
            color: '#57629E'
          },
          color: "#FFF"
        }
      }
    },
    MuiDatePickerToolbar: {
      styleOverrides: {
        title: {
          color: '#FFF'
        }
      }
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: '#FFF',
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.secondary.main
          }
        }),
        today: {
          "&:not(.Mui-selected)": {
            borderColor: '#FFF'
          }
        }
      }
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: '#FFF'
        }
      }
    },
    MuiPickersToolbar: {
      styleOverrides: {
        root: {
          '& .MuiTypography-overline': {
            color: '#FFF'
          }
        }
      }
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          color: '#FFF'
        },
        // La flecha junto al mes abre la lista de años (la que más se usa en la fecha de nacimiento)
        switchViewButton: {
          color: '#FFF'
        }
      }
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          color: '#FFF'
        },
        selected: ({ theme }) => ({
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.secondary.main,
          '&:focus, &:hover': {
            backgroundColor: theme.palette.secondary.main
          }
        })
      }
    }
  },
});

// Los calendarios en español: index.js los envuelve a todos con <LocalizationProvider {...calendario}>. Los meses y los días en
// español, la semana empieza en domingo (como el calendario de las reservas) y los textos de MUI también («Seleccionar fecha»,
// «Cancelar», «DD-MM-AAAA»)
const calendario = {
  dateAdapter: AdapterDateFns,
  adapterLocale: { ...es, options: { ...es.options, weekStartsOn: 0 } },
  localeText: esES.components.MuiLocalizationProvider.defaultProps.localeText,
};

export { theme, StyledRating, calendario };